/**
 * Bindeglied zwischen dem, was gespeichert ist, und dem, was der Geometriekern
 * rechnet.
 *
 * Gespeichert wird nur die Eingabe: Polygon, Traufkante, Neigung, Regeln und
 * die von Hand entfernten Plaetze. Das Raster selbst wird jedes Mal neu
 * gerechnet. Damit kann es keinen Stand geben, in dem die gespeicherte
 * Modulanzahl nicht mehr zur Flaeche passt — der Fehler, vor dem PV-E03 warnt.
 */

import {
  analysiereFlaeche,
  belege,
  bezugspunkt,
  abmessungen,
  kennzahlen,
  winkelabweichungGrad,
  reserve,
  nachDachebene,
  nachGeo,
  nachGrundrissebene,
  huellkasten,
  kantenlaenge,
  nachGrundriss,
  punktInnerhalb,
  ringNachGrundriss,
  schwerpunkt,
  skaliere,
  spanneFassadeAuf,
  vergleicheAusrichtungen,
  weiteAuf,
  dreheHindernis,
  hindernismass,
  hindernisRechteck,
  randabstaende,
  verschiebeRing,
  traegerlinien,
  type Hindernismass,
  type Traegerlagen,
  type Randabstaende,
  type Belegung as Rasterbelegung,
  type Flaechenkennzahlen,
  type Rasterabmessungen,
  type Rasterreserve,
  type Geo,
  type Kennzahlen,
  type Modulplatz,
  type Punkt,
  type Rasterregeln,
  type Ring,
} from '@pv/geometrie';

import type { Belegung, Flaeche, Modul } from './typen.ts';

/**
 * Die Modulfelder einer Belegung. Gespeichert war anfangs ein einzelnes Feld
 * (eine Liste von Punkten), seit 0.17 eine Liste von Feldern — gelesen wird
 * beides, deshalb braucht es keine Migration, und ein Gerät mit offenen
 * Änderungen bekommt keinen Konflikt.
 */
export function modulfelderVon(belegung: Pick<Belegung, 'modulfeld'> | null | undefined): Geo[][] {
  const roh = belegung?.modulfeld;
  if (!roh || roh.length === 0) return [];
  const felder = 'lat' in (roh[0] as object) ? [roh as Geo[]] : (roh as Geo[][]);
  return felder.filter((f) => Array.isArray(f) && f.length >= 3);
}

export const platzSchluessel = (platz: { reihe: number; spalte: number }): string =>
  `${platz.reihe}:${platz.spalte}`;

/**
 * Die Datenbank schreibt breite_mm, der Geometriekern kennt breiteMm. Die
 * Umrechnung steht genau hier, damit der Kern nichts von der Ablage weiss.
 */
function alsModulmass(modul: Modul): {
  breiteMm: number;
  hoeheMm: number;
  nennleistungWp: number;
} {
  return {
    breiteMm: modul.breite_mm,
    hoeheMm: modul.hoehe_mm,
    nennleistungWp: modul.nennleistung_wp,
  };
}

export function regelnAus(belegung: Belegung, flaeche: Flaeche): Rasterregeln {
  const regeln: Rasterregeln = {
    ausrichtung: belegung.ausrichtung,
    randTraufeMm: belegung.regeln.randTraufeMm,
    randFirstMm: belegung.regeln.randFirstMm,
    randOrtgangMm: belegung.regeln.randOrtgangMm,
    fugeQuerMm: belegung.regeln.fugeQuerMm,
    fugeLaengsMm: belegung.regeln.fugeLaengsMm,
    sicherheitsabstandMm: belegung.regeln.sicherheitsabstandMm,
  };
  if (belegung.regeln.feldlage) regeln.feldlage = belegung.regeln.feldlage;
  if (belegung.regeln.versatzU) regeln.versatzU = belegung.regeln.versatzU;
  if (belegung.regeln.versatzV) regeln.versatzV = belegung.regeln.versatzV;
  if (belegung.regeln.drehungGrad) regeln.drehungGrad = belegung.regeln.drehungGrad;
  if (belegung.regeln.trennung) regeln.trennung = belegung.regeln.trennung;
  // Flachdach: die Flaeche liegt flach, die Module stehen steiler (PV-D14).
  if (flaeche.aufstaenderung_grad) {
    regeln.aufstaenderung = {
      aufstaenderungGrad: flaeche.aufstaenderung_grad,
      sonnenstandGrad: flaeche.sonnenstand_grad ?? 17,
    };
  }
  return regeln;
}

export interface Planung {
  bezug: Geo;
  /** Polygon im Grundriss, bereits auf das Referenzmass skaliert. */
  grundriss: Ring;
  kennzahlen: Flaechenkennzahlen;
  raster: Rasterbelegung;
  /** Die gesetzten Module, ohne die von Hand entfernten. */
  gesetzt: Modulplatz[];
  /**
   * Die von Hand entfernten Plaetze — als Geister auf der Karte.
   *
   * Vorher wurden sie gar nicht gezeichnet. Ein Tipp aufs Modul liess es
   * verschwinden, und danach war die Stelle leeres Dach: kein Hinweis, dass
   * dort ein Platz frei geworden war, und vor allem kein Weg zurueck, weil ein
   * Klick nur trifft, was auch gezeichnet ist. Es blieb nur "alle
   * zuruecksetzen".
   */
  entfernte: Modulplatz[];
  zahlen: Kennzahlen;
  /** Module als Geo-Polygone, fertig zum Zeichnen auf der Karte. */
  moduleGeo: Geo[][];
  /** Dasselbe fuer die entfernten Plaetze, in derselben Reihenfolge. */
  entfernteGeo: Geo[][];
  /**
   * Der Rand, der um jede Aussparung frei bleibt (Sicherheitsabstand), im
   * Luftbild. Ohne ihn sah es aus, als fehlten um ein Dachfenster herum
   * grundlos Module.
   */
  sperrrandGeo: Geo[][];
  /** Was hoch und was quer bringt — damit die guenstigere Variante sofort sichtbar ist. */
  vergleich?: { hoch: number; quer: number };
  /** Dachmasse und die Raender, die wirklich frei bleiben (PV-F08). */
  masse: Rasterabmessungen;
  /** Was eine weitere Reihe oder Spalte braeuchte — null ohne Modul. */
  reserve: Rasterreserve | null;
  /** Groesste Abweichung einer Kante von 0 oder 90 Grad, in der Dachebene. */
  winkelabweichungGrad: number;
  /**
   * Die Modulfelder in der Dachebene, wenn welche gezeichnet sind. Bei genau
   * einem bezieht sich die Reserve auf das Feld; bei mehreren gibt es keine —
   * „frei“ hätte dort keinen Ort.
   */
  modulfelder?: Ring[];
  /** Nur bei Fassaden: die Module in der Ansicht, nicht auf der Karte. */
  ansicht?: { breiteM: number; hoeheM: number; module: Modulplatz[] };
}

/**
 * Der Weg zwischen Luftbild und Dachebene, fuer eine Flaeche.
 *
 * Luftbild (Geo) -> Grundriss in Metern -> Referenzmass -> Dachebene, und
 * zurueck. An einer Stelle, damit Flaeche, Aussparungen und Module ihn gleich
 * gehen: Zeigt das Referenzmass, dass das Luftbild um 5 % zu klein misst,
 * wird in der Dachebene mit der wahren Groesse gerechnet — gezeichnet wird
 * aber wieder im Luftbild, sonst ragten die Module ueber die gezeichnete
 * Flaeche hinaus.
 */
export interface Dachbezug {
  bezug: Geo;
  /** Grundriss in wahrer Groesse, also nach dem Referenzmass. */
  grundriss: Ring;
  lage: Flaechenkennzahlen;
  zuDach: (p: Geo) => Punkt;
  zuGeo: (p: Punkt) => Geo;
}

export function dachbezug(flaeche: Flaeche): Dachbezug | null {
  if (flaeche.art === 'fassade' || flaeche.polygon.length < 3) return null;
  const bezug = bezugspunkt(flaeche.polygon);
  const imBild = ringNachGrundriss(flaeche.polygon, bezug);

  let faktor = 1;
  const zentrum = schwerpunkt(imBild);
  if (flaeche.referenzmass) {
    const ist = kantenlaenge(imBild, flaeche.referenzmass.kanteIndex);
    if (ist > 1e-9 && flaeche.referenzmass.laengeM > 0) faktor = flaeche.referenzmass.laengeM / ist;
  }
  const grundriss = faktor === 1 ? imBild : skaliere(imBild, faktor, zentrum);

  const lage = analysiereFlaeche(
    grundriss,
    flaeche.traufkante_index ?? 0,
    flaeche.neigung_grad,
    flaeche.azimut_ueberschrieben ?? undefined,
  );
  const ausrichtung = { azimutGrad: lage.azimutGrad, neigungGrad: flaeche.neigung_grad };

  return {
    bezug,
    grundriss,
    lage,
    zuDach: (p) => {
      const g = nachGrundriss(p, bezug);
      const wahr =
        faktor === 1
          ? g
          : { x: zentrum.x + (g.x - zentrum.x) * faktor, y: zentrum.y + (g.y - zentrum.y) * faktor };
      return nachDachebene(wahr, ausrichtung);
    },
    zuGeo: (p) => {
      const wahr = nachGrundrissebene(p, ausrichtung);
      const imBildPunkt =
        faktor === 1
          ? wahr
          : {
              x: zentrum.x + (wahr.x - zentrum.x) / faktor,
              y: zentrum.y + (wahr.y - zentrum.y) / faktor,
            };
      return nachGeo(imBildPunkt, bezug);
    },
  };
}

/**
 * Eine Strecke auf dem Dach in wahrer Länge — wenn beide Enden auf derselben
 * Dachfläche liegen. Das Luftbild zeigt die Projektion: Quer zur Traufe ist ein
 * 30-Grad-Dach um 15 Prozent länger, als es aussieht (Spec: 8,00 m im Bild sind
 * 9,24 m am Dach). Eine Bemaßung für die Montage muss das Dachmaß tragen.
 *
 * Gerechnet über `dachbezug()`, also mit Referenzmaß — derselbe Weg wie für
 * Module und Aussparungen. Liegen die Enden nicht auf einer Dachfläche, gibt es
 * kein Dachmaß, und es bleibt beim Maß im Luftbild.
 */
export function dachstrecke(
  flaechen: Flaeche[],
  a: Geo,
  b: Geo,
): { laengeM: number; flaeche: Flaeche } | null {
  for (const flaeche of flaechen) {
    if (flaeche.art === 'fassade' || flaeche.polygon.length < 3) continue;
    const ring = flaeche.polygon.map((p) => ({ x: p.lng, y: p.lat }));
    // Die Kante zählt als innen: Gemessen wird fast immer von Ecke zu Ecke.
    if (!punktInnerhalb({ x: a.lng, y: a.lat }, ring) || !punktInnerhalb({ x: b.lng, y: b.lat }, ring)) {
      continue;
    }
    const bezug = dachbezug(flaeche);
    if (!bezug) continue;
    const pa = bezug.zuDach(a);
    const pb = bezug.zuDach(b);
    return { laengeM: Math.hypot(pb.x - pa.x, pb.y - pa.y), flaeche };
  }
  return null;
}

/**
 * Ein Hindernis, wie es auf dem Dach liegt: Mass in der eigenen Lage, Drehung
 * gegen die Traufe, Abstaende zu Traufe, First und Ortgang.
 *
 * Auf einem Luftbild mit 29 cm je Bildpunkt laesst sich ein Dachfenster nicht
 * genau zeichnen — im ersten Test bei Wania kamen 144 x 184 cm und
 * 222 x 180 cm heraus, und mit 30 cm Sicherheitsabstand ringsum fehlten Module
 * auf ueber 2 x 2,4 m. Deshalb stehen Mass und Lage dran, und sie lassen sich
 * setzen — so, wie der Monteur sie mit dem Massband abnimmt.
 */
export interface Hindernislage {
  mass: Hindernismass;
  abstaende: Randabstaende;
}

export function hindernislage(flaeche: Flaeche, index: number): Hindernislage | null {
  const weg = dachbezug(flaeche);
  const ring = flaeche.aussparungen[index];
  if (!weg || !ring || ring.length < 3) return null;
  const imDach = ring.map(weg.zuDach);
  const mass = hindernismass(imDach);
  if (!mass) return null;
  return { mass, abstaende: randabstaende(flaeche.polygon.map(weg.zuDach), imDach) };
}

export type Hindernisaenderung =
  | { breiteM?: number; hoeheM?: number }
  | { drehungGrad: number }
  | { rand: keyof Randabstaende; abstandM: number };

/**
 * Das Hindernis mit neuem Mass, neuer Drehung oder neuem Randabstand.
 *
 * Beim Mass bleiben Traufe und linker Ortgang stehen, nicht die Mitte: Wer
 * zuerst „2,40 von der Traufe“ eintraegt und danach die Groesse, will die
 * 2,40 behalten. Ein Mass macht aus einer gezeichneten Form ein Rechteck.
 */
export function aendereHindernis(
  flaeche: Flaeche,
  index: number,
  aenderung: Hindernisaenderung,
): Geo[] | null {
  const weg = dachbezug(flaeche);
  const ring = flaeche.aussparungen[index];
  const lage = hindernislage(flaeche, index);
  if (!weg || !ring || !lage) return null;
  const imDach = ring.map(weg.zuDach);

  let neu: Punkt[];
  if ('drehungGrad' in aenderung) {
    neu = dreheHindernis(imDach, aenderung.drehungGrad - lage.mass.drehungGrad);
  } else if ('rand' in aenderung) {
    const ist = lage.abstaende[aenderung.rand];
    if (ist === null) return null;
    const d = aenderung.abstandM - ist;
    const schub = {
      traufeM: { x: 0, y: d },
      firstM: { x: 0, y: -d },
      linksM: { x: d, y: 0 },
      rechtsM: { x: -d, y: 0 },
    }[aenderung.rand];
    neu = verschiebeRing(imDach, schub.x, schub.y);
  } else {
    // Nur das genannte Mass aendert sich, das andere kommt aus dem Ring —
    // nicht aus einem Feld, das noch den Stand von vorhin zeigt.
    const breiteM = aenderung.breiteM ?? lage.mass.breiteM;
    const hoeheM = aenderung.hoeheM ?? lage.mass.hoeheM;
    if (breiteM <= 0 || hoeheM <= 0) return null;
    const vorher = huellkasten(imDach);
    const rechteck = hindernisRechteck(lage.mass.mitte, breiteM, hoeheM, lage.mass.drehungGrad);
    const nachher = huellkasten(rechteck);
    neu = verschiebeRing(rechteck, vorher.minX - nachher.minX, vorher.minY - nachher.minY);
  }
  return neu.map(weg.zuGeo);
}

/**
 * Sparren und Pfetten einer Flaeche: die Linien zum Zeichnen und die Lagen in
 * der Dachebene, auf die die Materialliste die Haken setzt.
 *
 * Nicht bei Fassaden und nicht beim Flachdach — dort steht die Unterkonstruktion
 * auf dem Dach, nicht auf Sparren (Mathias, 11/2026). Ohne eingetragenen
 * Abstand gibt es nichts.
 */
export function traegerFuer(
  flaeche: Flaeche,
): { linien: Array<{ art: 'sparren' | 'pfetten'; punkte: Geo[] }>; lagen: Traegerlagen } | null {
  const angabe = flaeche.tragwerk;
  if (!angabe || flaeche.art === 'fassade' || flaeche.dachtyp === 'flachdach') return null;
  if (!angabe.sparrenabstandMm && !angabe.pfettenabstandMm) return null;
  const weg = dachbezug(flaeche);
  if (!weg) return null;
  const mm = (wert: number | null | undefined): number | null => (wert != null ? wert / 1000 : null);
  const linien = traegerlinien(weg.lage.dachebene, {
    sparrenabstandM: mm(angabe.sparrenabstandMm),
    ersterSparrenM: mm(angabe.ersterSparrenMm),
    pfettenabstandM: mm(angabe.pfettenabstandMm),
    erstePfetteM: mm(angabe.erstePfetteMm),
  });
  return {
    linien: [
      ...linien.sparren.map(([a, b]) => ({ art: 'sparren' as const, punkte: [weg.zuGeo(a), weg.zuGeo(b)] })),
      ...linien.pfetten.map(([a, b]) => ({ art: 'pfetten' as const, punkte: [weg.zuGeo(a), weg.zuGeo(b)] })),
    ],
    lagen: { sparrenX: linien.sparrenX, pfettenY: linien.pfettenY },
  };
}

/**
 * Zuletzt gerechnete Planungen, nach ihren Eingaben.
 *
 * Die Projektansicht rechnet bei jeder Aenderung alle Flaechen neu. Seit das
 * Raster seine beste Lage sucht, kostet eine Flaeche rund 30 ms statt einer —
 * bei vier Flaechen spuerbar bei jedem Tastendruck. Gerechnet wird deshalb nur,
 * was sich geaendert hat. Der Schluessel enthaelt alles, was in die Rechnung
 * eingeht, und nichts sonst: Ein Zeitstempel oder ein Abgleichstand duerfen
 * keine neue Rechnung ausloesen.
 */
const ZWISCHENSPEICHER = new Map<string, Planung | undefined>();
const ZWISCHENSPEICHER_GROESSE = 64;

function schluesselFuer(
  flaeche: Flaeche,
  belegung: Belegung | undefined,
  modul: Modul | undefined,
): string {
  return JSON.stringify([
    flaeche.art,
    flaeche.polygon,
    flaeche.aussparungen,
    flaeche.traufkante_index,
    flaeche.neigung_grad,
    flaeche.azimut_ueberschrieben,
    flaeche.referenzmass,
    flaeche.aufstaenderung_grad,
    flaeche.sonnenstand_grad,
    flaeche.fassade,
    belegung
      ? [belegung.ausrichtung, belegung.regeln, belegung.entfernte_plaetze, belegung.modulfeld ?? null]
      : null,
    modul ? [modul.id, modul.breite_mm, modul.hoehe_mm, modul.nennleistung_wp] : null,
  ]);
}

export function rechne(
  flaeche: Flaeche,
  belegung: Belegung | undefined,
  modul: Modul | undefined,
): Planung | undefined {
  const schluessel = schluesselFuer(flaeche, belegung, modul);
  if (ZWISCHENSPEICHER.has(schluessel)) return ZWISCHENSPEICHER.get(schluessel);
  const ergebnis = rechneNeu(flaeche, belegung, modul);
  if (ZWISCHENSPEICHER.size >= ZWISCHENSPEICHER_GROESSE) {
    // Map haelt die Einfuegereihenfolge — der erste Eintrag ist der aelteste.
    ZWISCHENSPEICHER.delete(ZWISCHENSPEICHER.keys().next().value!);
  }
  ZWISCHENSPEICHER.set(schluessel, ergebnis);
  return ergebnis;
}

function rechneNeu(
  flaeche: Flaeche,
  belegung: Belegung | undefined,
  modul: Modul | undefined,
): Planung | undefined {
  if (flaeche.art === 'fassade') return rechneFassade(flaeche, belegung, modul);
  if (flaeche.polygon.length < 3) return undefined;

  const { bezug, grundriss, lage, zuDach, zuGeo } = dachbezug(flaeche)!;

  const leerRaster = { module: [], nutzflaeche: [], reihen: 0, spalten: 0 };
  const leer: Planung = {
    bezug,
    grundriss,
    kennzahlen: lage,
    // Auch ohne Modul zaehlt, wie gross das Dach ist — das ist die erste Zahl,
    // die vor Ort gebraucht wird.
    masse: abmessungen(lage.dachebene, leerRaster),
    reserve: null,
    winkelabweichungGrad: winkelabweichungGrad(lage.dachebene),
    raster: leerRaster,
    gesetzt: [],
    entfernte: [],
    zahlen: { anzahl: 0, leistungKwp: 0, belegteFlaecheM2: 0, belegungsgrad: 0 },
    moduleGeo: [],
    entfernteGeo: [],
    sperrrandGeo: [],
  };
  if (!belegung || !modul) return leer;

  // Aussparungen gehen denselben Weg wie die Flaeche — Grundriss, Referenzmass,
  // Dachebene. Vorher wurden sie nicht mit dem Referenzmass skaliert: Mit
  // einem Referenzmass wuchs das Dach, der Kamin blieb klein und rutschte weg.
  const aussparungen = flaeche.aussparungen.map((ring) => ring.map(zuDach));

  // Die Modulfelder gehen denselben Weg wie die Hindernisse.
  const felder = modulfelderVon(belegung).map((ring) => ring.map(zuDach));
  const modulfeld = felder.length === 1 ? felder[0] : undefined;

  const regeln = regelnAus(belegung, flaeche);
  const beide = vergleicheAusrichtungen(
    lage.dachebene,
    aussparungen,
    alsModulmass(modul),
    regeln,
    felder,
  );
  const raster = beide[belegung.ausrichtung];
  const entfernt = new Set(belegung.entfernte_plaetze);
  const gesetzt = raster.module.filter((platz) => !entfernt.has(platzSchluessel(platz)));
  const entfernte = raster.module.filter((platz) => entfernt.has(platzSchluessel(platz)));
  // Im Modulfeld zaehlt, was im Feld noch frei ist — der Rand zum Dach ist
  // dort schon abgegolten, das Feld hat ihn beim Zeichnen.
  const reserveImFeld = modulfeld
    ? reserve(abmessungen(modulfeld, { ...raster, module: gesetzt }), alsModulmass(modul), {
        ...regeln,
        randTraufeMm: 0,
        randFirstMm: 0,
        randOrtgangMm: 0,
      })
    : undefined;

  return {
    bezug,
    grundriss,
    kennzahlen: lage,
    raster,
    gesetzt,
    entfernte,
    zahlen: kennzahlen({ ...raster, module: gesetzt }, alsModulmass(modul), lage.wahreFlaecheM2),
    // Gerechnet wird ueber die gesetzten Module, nicht ueber das rohe Raster:
    // Wer von Hand eine Reihe entfernt, will den dann groesseren Rand sehen.
    masse: abmessungen(lage.dachebene, { ...raster, module: gesetzt }),
    reserve:
      reserveImFeld !== undefined
        ? reserveImFeld
        : felder.length > 1
          ? null
          : reserve(abmessungen(lage.dachebene, { ...raster, module: gesetzt }), alsModulmass(modul), regeln),
    winkelabweichungGrad: winkelabweichungGrad(lage.dachebene),
    ...(felder.length > 0 ? { modulfelder: felder } : {}),
    moduleGeo: gesetzt.map((platz) => platz.ecken.map(zuGeo)),
    entfernteGeo: entfernte.map((platz) => platz.ecken.map(zuGeo)),
    sperrrandGeo:
      (regeln.sicherheitsabstandMm ?? 0) > 0
        ? aussparungen
            .filter((ring) => ring.length >= 3)
            .map((ring) => weiteAuf(ring, (regeln.sicherheitsabstandMm ?? 0) / 1000).map(zuGeo))
        : [],
    vergleich: { hoch: beide.hoch.module.length, quer: beide.quer.module.length },
  };
}

function rechneFassade(
  flaeche: Flaeche,
  belegung: Belegung | undefined,
  modul: Modul | undefined,
): Planung | undefined {
  if (!flaeche.fassade) return undefined;
  const bezug = bezugspunkt([flaeche.fassade.a, flaeche.fassade.b]);
  const [a, b] = ringNachGrundriss([flaeche.fassade.a, flaeche.fassade.b], bezug);
  if (!a || !b) return undefined;

  const wand = spanneFassadeAuf({
    a,
    b,
    hoeheM: flaeche.fassade.hoeheM,
    seite: flaeche.fassade.seite,
    ...(flaeche.fassade.sockelhoeheM !== undefined
      ? { sockelhoeheM: flaeche.fassade.sockelhoeheM }
      : {}),
    neigungGrad: flaeche.neigung_grad,
  });

  const kennzahlenLeer: Kennzahlen = {
    anzahl: 0,
    leistungKwp: 0,
    belegteFlaecheM2: 0,
    belegungsgrad: 0,
  };
  const lage: Flaechenkennzahlen = {
    azimutGrad: flaeche.azimut_ueberschrieben ?? wand.azimutGrad,
    neigungGrad: wand.neigungGrad,
    projizierteFlaecheM2: 0,
    wahreFlaecheM2: wand.flaecheM2,
    dachebene: wand.ansicht,
  };

  if (!belegung || !modul) {
    return {
      bezug,
      masse: abmessungen(wand.ansicht, { module: [], nutzflaeche: [], reihen: 0, spalten: 0 }),
      reserve: null,
      // Die Fassadenansicht wird als Rechteck aufgespannt, da gibt es nichts
      // zu begradigen.
      winkelabweichungGrad: 0,
      grundriss: [a, b],
      kennzahlen: lage,
      raster: { module: [], nutzflaeche: [], reihen: 0, spalten: 0 },
      gesetzt: [],
      entfernte: [],
      zahlen: kennzahlenLeer,
      moduleGeo: [],
      entfernteGeo: [],
      sperrrandGeo: [],
      ansicht: { breiteM: wand.laengeM, hoeheM: flaeche.fassade.hoeheM, module: [] },
    };
  }

  // Aussparungen der Fassade liegen bereits in der Ansicht (PV-G04).
  const aussparungen = flaeche.aussparungen.map((ring) =>
    ring.map((p) => ({ x: p.lng, y: p.lat })),
  );
  const raster = belege(
    wand.ansicht,
    aussparungen,
    alsModulmass(modul),
    regelnAus(belegung, flaeche),
  );
  const entfernt = new Set(belegung.entfernte_plaetze);
  const gesetzt = raster.module.filter((platz) => !entfernt.has(platzSchluessel(platz)));

  return {
    bezug,
    grundriss: [a, b],
    kennzahlen: lage,
    raster,
    gesetzt,
    // Die Fassade wird nicht auf der Karte belegt, sondern in der Ansicht.
    entfernte: raster.module.filter((platz) => entfernt.has(platzSchluessel(platz))),
    zahlen: kennzahlen({ ...raster, module: gesetzt }, alsModulmass(modul), wand.flaecheM2),
    masse: abmessungen(wand.ansicht, { ...raster, module: gesetzt }),
    reserve: reserve(
      abmessungen(wand.ansicht, { ...raster, module: gesetzt }),
      alsModulmass(modul),
      regelnAus(belegung, flaeche),
    ),
    winkelabweichungGrad: 0,
    moduleGeo: [],
    entfernteGeo: [],
    sperrrandGeo: [],
    ansicht: { breiteM: wand.laengeM, hoeheM: flaeche.fassade.hoeheM, module: gesetzt },
  };
}

/** Summe ueber alle Flaechen eines Projekts — die Zahl, die oben steht (PV-F08). */
export function summiere(planungen: Planung[]): Kennzahlen {
  return planungen.reduce<Kennzahlen>(
    (summe, planung) => ({
      anzahl: summe.anzahl + planung.zahlen.anzahl,
      leistungKwp: summe.leistungKwp + planung.zahlen.leistungKwp,
      belegteFlaecheM2: summe.belegteFlaecheM2 + planung.zahlen.belegteFlaecheM2,
      belegungsgrad: 0,
    }),
    { anzahl: 0, leistungKwp: 0, belegteFlaecheM2: 0, belegungsgrad: 0 },
  );
}
